import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaGavel } from 'react-icons/fa';
import axios from 'axios';

const headerCell = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300';
const bodyCell = 'px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-100';
const mutedCell = 'px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300';

const Salg = () => {
  const [biler, setBiler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBil, setEditingBil] = useState(null);
  const [formData, setFormData] = useState({
    internt_nummer: '',
    vogn_type: '',
    status: 'Til salg',
    pris: '',
    beskrivelse: '',
    listing_image: ''
  });
  const [createQuery, setCreateQuery] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createResults, setCreateResults] = useState([]);

  const [auctions, setAuctions] = useState([]);
  const [auctionsLoading, setAuctionsLoading] = useState(true);
  const [auctionModal, setAuctionModal] = useState(false);
  const [bidsModal, setBidsModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [auctionBids, setAuctionBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidsError, setBidsError] = useState('');
  const [auctionForm, setAuctionForm] = useState({
    query: '',
    selectedBil: null,
    min_price: '',
    start_price: '',
    duration_hours: 72,
    ends_at: '',
    listing_image: ''
  });
  const [auctionSearchLoading, setAuctionSearchLoading] = useState(false);
  const [auctionSearchError, setAuctionSearchError] = useState('');
  const [auctionResults, setAuctionResults] = useState([]);

  useEffect(() => {
    fetchBiler();
    fetchAuctions();
  }, []);

  const fetchBiler = async () => {
    try {
      const response = await axios.get('/api/biler/salg');
      setBiler(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching biler:', error);
      setLoading(false);
    }
  };

  const fetchAuctions = async () => {
    try {
      setAuctionsLoading(true);
      const response = await axios.get('/api/auktioner?includeEnded=true');
      setAuctions(response.data || []);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    } finally {
      setAuctionsLoading(false);
    }
  };

  const selectAuctionBil = async (bil) => {
    setAuctionForm((prev) => ({ ...prev, selectedBil: bil, listing_image: '' }));
    try {
      const res = await axios.get(`/api/biler/${bil.id}`);
      const listingImage = res.data?.listing_image || '';
      setAuctionForm((prev) => ({ ...prev, selectedBil: bil, listing_image: listingImage }));
    } catch (_) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBil) {
        await axios.put(`/api/biler/${editingBil.id}`, formData);
      } else {
        await axios.post('/api/biler', formData);
      }
      setShowModal(false);
      setEditingBil(null);
      setFormData({
        internt_nummer: '',
        vogn_type: '',
        status: 'Til salg',
        pris: '',
        beskrivelse: '',
        listing_image: ''
      });
      fetchBiler();
    } catch (error) {
      console.error('Error saving bil:', error);
    }
  };

  const handleEdit = (bil) => {
    setEditingBil(bil);
    setFormData({
      internt_nummer: bil.internt_nummer || '',
      vogn_type: bil.vogn_type || '',
      status: bil.status || 'Til salg',
      pris: bil.pris || '',
      beskrivelse: bil.beskrivelse || '',
      listing_image: bil.listing_image || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne bil?')) {
      try {
        await axios.delete(`/api/biler/${id}`);
        fetchBiler();
      } catch (error) {
        console.error('Error deleting bil:', error);
      }
    }
  };

  const filteredBiler = useMemo(() => biler.filter((bil) =>
    bil.internt_nummer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bil.vogn_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bil.status?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [biler, searchTerm]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Til salg': return 'bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-200';
      case 'Solgt': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-200';
      case 'Reserveret': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/70 dark:text-amber-100';
      case 'På auktion': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/70 dark:text-purple-100';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100';
    }
  };

  const statusLabel = (auction) => {
    const ends = auction.ends_at ? new Date(auction.ends_at) : null;
    const isActive = auction.status === 'active' && ends && ends > new Date();
    if (!ends) return 'Status ukendt';
    if (!isActive) return 'Afsluttet';
    const diffMs = ends - Date.now();
    const hours = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60)));
    return `${hours} t tilbage`;
  };

  const openAuctionBids = async (auction) => {
    setSelectedAuction(auction);
    setBidsModal(true);
    setBidsLoading(true);
    setAuctionBids([]);
    setBidsError('');
    try {
      const res = await axios.get(`/api/auktioner/${auction.id}/bids`);
      setAuctionBids(res.data || []);
    } catch (error) {
      console.error('Error fetching bids:', error);
      setBidsError('Kunne ikke hente bud for auktionen');
    } finally {
      setBidsLoading(false);
    }
  };

  const handleAuctionSubmit = async (e) => {
    e.preventDefault();
    if (!auctionForm.selectedBil) {
      setAuctionSearchError('Vælg en bil til auktionen');
      return;
    }

    try {
      if (auctionForm.listing_image) {
        await axios.put(`/api/biler/${auctionForm.selectedBil.id}`, { listing_image: auctionForm.listing_image });
      }
      await axios.post('/api/auktioner', {
        bil_id: auctionForm.selectedBil.id,
        min_price: auctionForm.min_price ? Number(auctionForm.min_price) : null,
        start_price: auctionForm.start_price ? Number(auctionForm.start_price) : null,
        duration_hours: auctionForm.duration_hours,
        ends_at: auctionForm.ends_at || undefined
      });
      setAuctionModal(false);
      setAuctionForm({ query: '', selectedBil: null, min_price: '', start_price: '', duration_hours: 72, ends_at: '', listing_image: '' });
      setAuctionResults([]);
      fetchAuctions();
      fetchBiler();
    } catch (error) {
      console.error('Error creating auction:', error);
      setAuctionSearchError('Kunne ikke oprette auktionen');
    }
  };

  const handleCloseAuction = async (auctionId) => {
    try {
      await axios.put(`/api/auktioner/${auctionId}`, { status: 'ended', bil_status: 'Til salg' });
      fetchAuctions();
      fetchBiler();
    } catch (error) {
      console.error('Error ending auction:', error);
    }
  };

  const handleDeleteAuction = async (auctionId) => {
    if (!window.confirm('Slet auktionen og fjern den fra listen?')) return;
    try {
      await axios.delete(`/api/auktioner/${auctionId}`);
      fetchAuctions();
      fetchBiler();
    } catch (error) {
      console.error('Error deleting auction:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 space-y-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Salg og auktioner</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">Administrer biler til salg og opret auktioner med varighed og minimumspris.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setAuctionModal(true); setAuctionSearchError(''); setAuctionResults([]); setAuctionForm({ query: '', selectedBil: null, min_price: '', start_price: '', duration_hours: 72, ends_at: '', listing_image: '' }); }}
            className="bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-800 transition-colors"
          >
            <FaGavel /> Opret auktion
          </button>
          <button
            onClick={() => { setEditingBil(null); setFormData({ internt_nummer: '', vogn_type: '', status: 'Til salg', pris: '', beskrivelse: '', listing_image: '' }); setShowModal(true); }}
            className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <FaPlus /> Tilføj bil
          </button>
        </div>
      </div>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Biler til salg</h2>
        <div className="mb-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Søg efter biler..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
          <div className="scroll-fade-x"><div className="scroll-area">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className={headerCell}>Internt nummer</th>
                  <th className={headerCell}>Billede</th>
                  <th className={headerCell}>Vogn type</th>
                  <th className={headerCell}>Status</th>
                  <th className={headerCell}>Pris</th>
                  <th className={headerCell}>Beskrivelse</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300">Handlinger</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {filteredBiler.map((bil) => (
                  <tr key={bil.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className={bodyCell}>{bil.internt_nummer}</td>
                    <td className={mutedCell}>
                      {bil.listing_image ? (
                        <img src={bil.listing_image} alt="Bilbillede" className="h-14 w-20 object-cover rounded border border-gray-200 dark:border-gray-700" />
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Ingen</span>
                      )}
                    </td>
                    <td className={mutedCell}>{bil.vogn_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(bil.status)}`}>
                        {bil.status}
                      </span>
                    </td>
                    <td className={mutedCell}>{bil.pris ? `kr ${bil.pris}` : '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-200">{bil.beskrivelse || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleEdit(bil)} className="text-falck-blue hover:text-blue-700">
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDelete(bil.id)} className="text-red-600 hover:text-red-500">
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <FaGavel className="text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Auktioner</h2>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
          <div className="scroll-fade-x"><div className="scroll-area">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className={headerCell}>Bil</th>
                  <th className={headerCell}>Billede</th>
                  <th className={headerCell}>Nummerplade</th>
                  <th className={headerCell}>Slutter</th>
                  <th className={headerCell}>Minimumspris</th>
                  <th className={headerCell}>Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300">Handlinger</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {auctionsLoading ? (
                  <tr><td colSpan="7" className="px-6 py-6 text-center text-gray-500 dark:text-gray-300">Henter auktioner...</td></tr>
                ) : auctions.length ? auctions.map((auction) => {
                  const ends = auction.ends_at ? new Date(auction.ends_at) : null;
                  return (
                    <tr
                      key={auction.id}
                      onClick={() => openAuctionBids(auction)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer"
                    >
                      <td className={bodyCell}>
                        <div className="font-semibold">{auction.internt_nummer || 'Ukendt'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-300">{auction.vogn_type || ''}</div>
                      </td>
                      <td className={mutedCell}>
                        {auction.listing_image ? (
                          <img src={auction.listing_image} alt="Auktionsbillede" className="h-14 w-20 object-cover rounded border border-gray-200 dark:border-gray-700" />
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">Ingen</span>
                        )}
                      </td>
                      <td className={mutedCell}>{auction.nummerplade || '-'}</td>
                      <td className={mutedCell}>{ends ? ends.toLocaleString('da-DK') : '-'}</td>
                      <td className={mutedCell}>{auction.min_price ? `kr ${auction.min_price}` : 'Ingen minimumspris'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${auction.status === 'active' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/70 dark:text-purple-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}`}>
                          {statusLabel(auction)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-3">
                        {auction.status === 'active' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCloseAuction(auction.id); }}
                            className="text-purple-700 hover:text-purple-600"
                          >
                            Afslut
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAuction(auction.id); }}
                          className="text-red-600 hover:text-red-500"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="7" className="px-6 py-6 text-center text-gray-500 dark:text-gray-300">Ingen auktioner endnu.</td></tr>
                )}
              </tbody>
            </table>
          </div></div>
        </div>
      </section>

      {bidsModal && selectedAuction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-5 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Bud på {selectedAuction.internt_nummer || 'auktion'}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">Se seneste bud og hvem der har budt.</p>
              </div>
              <button
                onClick={() => { setBidsModal(false); setSelectedAuction(null); setAuctionBids([]); setBidsError(''); }}
                className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700"
              >
                Luk
              </button>
            </div>

            {bidsLoading ? (
              <div className="text-center text-gray-600 dark:text-gray-300 py-6">Henter bud...</div>
            ) : bidsError ? (
              <div className="text-red-600 dark:text-red-400 py-4">{bidsError}</div>
            ) : auctionBids.length ? (
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className={headerCell}>Bud</th>
                      <th className={headerCell}>Budgiver</th>
                      <th className={headerCell}>Kontakt</th>
                      <th className={headerCell}>Tidspunkt</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {auctionBids.map((bid) => {
                      const created = bid.created_at ? new Date(bid.created_at) : null;
                      return (
                        <tr key={bid.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                          <td className={bodyCell}>{bid.amount ? `kr ${bid.amount}` : '-'}</td>
                          <td className={mutedCell}>{bid.bidder_name || 'Ukendt'}</td>
                          <td className={mutedCell}>{bid.bidder_contact || '-'}</td>
                          <td className={mutedCell}>{created ? created.toLocaleString('da-DK') : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-gray-600 dark:text-gray-300 py-6">Ingen bud endnu.</div>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              {editingBil ? 'Rediger bil' : 'Tilføj bil til salg'}
            </h2>
            {editingBil ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Internt nummer</label>
                  <input
                    type="text"
                    value={formData.internt_nummer}
                    onChange={(e) => setFormData({ ...formData, internt_nummer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Vogn type</label>
                  <input
                    type="text"
                    value={formData.vogn_type}
                    onChange={(e) => setFormData({ ...formData, vogn_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  >
                    <option value="Til salg">Til salg</option>
                    <option value="Solgt">Solgt</option>
                    <option value="Reserveret">Reserveret</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Pris (DKK)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pris}
                    onChange={(e) => setFormData({ ...formData, pris: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Beskrivelse</label>
                  <textarea
                    value={formData.beskrivelse}
                    onChange={(e) => setFormData({ ...formData, beskrivelse: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                    rows="3"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Billede til salg/auktion</label>
                  {formData.listing_image && (
                    <img src={formData.listing_image} alt="Bilbillede" className="h-28 w-full object-cover rounded-md border border-gray-200 dark:border-gray-700" />
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) return;
                        const reader = new FileReader();
                        reader.onload = () => setFormData((prev) => ({ ...prev, listing_image: reader.result }));
                        reader.readAsDataURL(file);
                      }}
                      className="w-full text-sm text-gray-700 dark:text-gray-200"
                    />
                    {formData.listing_image && (
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, listing_image: '' }))}
                        className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-md"
                      >
                        Fjern billede
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingBil(null);
                      setFormData({ internt_nummer: '', vogn_type: '', status: 'Til salg', pris: '', beskrivelse: '', listing_image: '' });
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Opdater
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Søg efter eksisterende bil via internt nummer eller nummerplade og sæt den til <strong>Til salg</strong>.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Søg bil</label>
                  <input
                    type="text"
                    value={createQuery}
                    onChange={async (e) => {
                      const q = e.target.value;
                      setCreateQuery(q);
                      setCreateError('');
                      if (q.length < 2) { setCreateResults([]); return; }
                      try {
                        setCreateLoading(true);
                        const res = await axios.get(`/api/search?q=${encodeURIComponent(q)}`);
                        const items = Array.isArray(res.data) ? res.data.filter(r => r.type === 'bil') : [];
                        setCreateResults(items);
                      } catch (err) {
                        setCreateError('Fejl ved søgning');
                        setCreateResults([]);
                      } finally {
                        setCreateLoading(false);
                      }
                    }}
                    placeholder="Internt nr. eller nummerplade"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Pris (DKK)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pris}
                    onChange={(e) => setFormData({ ...formData, pris: e.target.value })}
                    placeholder="Angiv pris for salg"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Billede til salgsannonce</label>
                  {formData.listing_image && (
                    <img src={formData.listing_image} alt="Bilbillede" className="h-24 w-full object-cover rounded-md border border-gray-200 dark:border-gray-700" />
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) return;
                        const reader = new FileReader();
                        reader.onload = () => setFormData((prev) => ({ ...prev, listing_image: reader.result }));
                        reader.readAsDataURL(file);
                      }}
                      className="w-full text-sm text-gray-700 dark:text-gray-200"
                    />
                    {formData.listing_image && (
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, listing_image: '' }))}
                        className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-md"
                      >
                        Fjern billede
                      </button>
                    )}
                  </div>
                </div>
                {createLoading && <div className="text-sm text-gray-500">Søger...</div>}
                {createError && <div className="text-sm text-red-600">{createError}</div>}
                <div className="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-md max-h-64 overflow-auto">
                  {createResults.map(r => (
                    <div key={`${r.type}-${r.id}`} className="p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{r.title}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-300">{r.subtitle}</div>
                      </div>
                      <button
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        onClick={async () => {
                          try {
                            setCreateError('');
                            const resBil = await axios.get(`/api/biler/${r.id}`);
                            const b = resBil.data || {};
                            await axios.put(`/api/biler/${r.id}`, {
                              internt_nummer: b.internt_nummer || '',
                              nummerplade: b.nummerplade || '',
                              model: b.model || '',
                              vogn_type: b.vogn_type || '',
                              status: 'Til salg',
                              pris: formData.pris ? Number(formData.pris) : null,
                              station_id: b.station_id || null,
                              type: b.type || 'Almindelig',
                              listing_image: formData.listing_image || b.listing_image || null
                            });
                            setShowModal(false);
                            setCreateQuery('');
                            setCreateResults([]);
                            setEditingBil(null);
                            fetchBiler();
                          } catch (e) {
                            setCreateError(e.response?.data?.message || 'Kunne ikke sætte bilen til salg');
                          }
                        }}
                      >
                        Tilføj til salg
                      </button>
                    </div>
                  ))}
                  {!createLoading && createQuery.length >= 2 && createResults.length === 0 && (
                    <div className="p-3 text-sm text-gray-600 dark:text-gray-300">Ingen biler fundet</div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingBil(null); setCreateQuery(''); setCreateResults([]); setFormData({ internt_nummer: '', vogn_type: '', status: 'Til salg', pris: '', beskrivelse: '', listing_image: '' }); }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300"
                  >
                    Luk
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {auctionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-none sm:rounded-lg p-4 sm:p-6 w-full max-w-lg shadow-xl mx-0 sm:mx-4 h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Opret auktion</h2>
            <form onSubmit={handleAuctionSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Find bil</label>
                <input
                  type="text"
                  value={auctionForm.query}
                  onChange={async (e) => {
                    const q = e.target.value;
                    setAuctionForm((prev) => ({ ...prev, query: q }));
                    setAuctionSearchError('');
                    if (q.length < 2) { setAuctionResults([]); return; }
                    try {
                      setAuctionSearchLoading(true);
                      const res = await axios.get(`/api/search?q=${encodeURIComponent(q)}`);
                      const items = Array.isArray(res.data) ? res.data.filter(r => r.type === 'bil') : [];
                      setAuctionResults(items);
                    } catch (err) {
                      setAuctionSearchError('Fejl ved søgning');
                      setAuctionResults([]);
                    } finally {
                      setAuctionSearchLoading(false);
                    }
                  }}
                  placeholder="Internt nr. eller nummerplade"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                />
                {auctionForm.selectedBil && (
                  <div className="mt-2 text-sm text-green-600 dark:text-green-300">
                    Valgt: {auctionForm.selectedBil.title}
                  </div>
                )}
              </div>
              {auctionSearchLoading && <div className="text-sm text-gray-500">Søger...</div>}
              {auctionSearchError && <div className="text-sm text-red-600">{auctionSearchError}</div>}
              <div className="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-md max-h-56 overflow-auto">
                {auctionResults.map(r => (
                  <div key={`${r.type}-${r.id}`} className="p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{r.title}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">{r.subtitle}</div>
                    </div>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm bg-purple-700 text-white rounded hover:bg-purple-800"
                      onClick={() => {
                        selectAuctionBil(r);
                        setAuctionSearchError('');
                      }}
                    >
                      Vælg
                    </button>
                  </div>
                ))}
                {!auctionSearchLoading && auctionForm.query.length >= 2 && auctionResults.length === 0 && (
                  <div className="p-3 text-sm text-gray-600 dark:text-gray-300">Ingen biler fundet</div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Billede til auktionen</label>
                {auctionForm.listing_image && (
                  <img src={auctionForm.listing_image} alt="Auktionsbillede" className="h-24 w-full object-cover rounded-md border border-gray-200 dark:border-gray-700" />
                )}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) return;
                      const reader = new FileReader();
                      reader.onload = () => setAuctionForm((prev) => ({ ...prev, listing_image: reader.result }));
                      reader.readAsDataURL(file);
                    }}
                    className="w-full text-sm text-gray-700 dark:text-gray-200"
                  />
                  {auctionForm.listing_image && (
                    <button
                      type="button"
                      onClick={() => setAuctionForm((prev) => ({ ...prev, listing_image: '' }))}
                      className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-md"
                    >
                      Fjern billede
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Minimumspris (DKK)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={auctionForm.min_price}
                    onChange={(e) => setAuctionForm((prev) => ({ ...prev, min_price: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Startpris (valgfri)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={auctionForm.start_price}
                    onChange={(e) => setAuctionForm((prev) => ({ ...prev, start_price: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Varighed (timer)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={auctionForm.duration_hours}
                    onChange={(e) => setAuctionForm((prev) => ({ ...prev, duration_hours: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Sluttidspunkt (overskriver varighed)</label>
                  <input
                    type="datetime-local"
                    value={auctionForm.ends_at}
                    onChange={(e) => setAuctionForm((prev) => ({ ...prev, ends_at: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setAuctionModal(false); setAuctionResults([]); setAuctionForm({ query: '', selectedBil: null, min_price: '', start_price: '', duration_hours: 72, ends_at: '', listing_image: '' }); }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300"
                >
                  Luk
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800"
                >
                  Gem auktion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Salg;
